// frontend/src/App.tsx
import { useEffect, useState, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ErrorBoundary } from "./errors/ErrorBoundary";
import { DevToolsPanel } from "./dev/DevToolsPanel";
import Onboarding from "./pages/Onboarding";

import RequireRole from "./components/RequireRole";
import { fetchIdentity, type Identity } from "./lib/auth";

import AdminRouter from "@/app/admin/AdminRouter";

import IngressRunnerPage from "./app/ingress/IngressRunnerPage";
import IngressChatPage from "./app/ingress/IngressChatPage";
import IngressLandingPage from "./app/ingress/IngressLandingPage";

// ✅ New (Phase 1 UI enforcement)
// These files exist per your earlier wiring list.
import { AuthProvider, useAuth } from "./app/auth/AuthProvider";
import LoginPage from "./app/pages/LoginPage";
import ForbiddenPage from "./app/pages/ForbiddenPage";
import ProvisionTenantPage from "./app/pages/ProvisionTenantPage";

// ---------- Lazy pages (real pages, no placeholders) ----------
const ChatPage = lazy(() => import("./pages/Chat"));
const AdminModels = lazy(() => import("./pages/admin/Models"));
const AdminPricing = lazy(() => import("./pages/admin/Pricing"));
const AdminPolicies = lazy(() => import("./pages/admin/Policies"));
const ObservabilityPage = lazy(() => import("./pages/admin/Observability"));
const MemoryPage = lazy(() => import("./pages/admin/Memory"));

// Workflow Composer (adapter onto React Flow UI)
const WorkflowComposerPage = lazy(() => import("./features/workflow/WorkflowComposerAdapter"));

// Dev-only: Feature Flags panel; no-op in prod
const FeatureFlagSwitchboard = import.meta.env.DEV
  ? lazy(() => import("./dev/FeatureFlagSwitchboard"))
  : lazy(async () => ({ default: () => null }));

const qc = new QueryClient();

/** Small wrapper to avoid 403 flicker while identity is still loading */
function AdminRoute(props: { identity: Identity | null; children: JSX.Element }) {
  const { identity, children } = props;
  if (!identity) {
    return <div style={{ padding: 16, fontSize: 14, color: "#555" }}>Loading identity…</div>;
  }
  // keep your existing behavior (does not break legacy identity paths)
  return (
    <RequireRole role="admin" identity={identity}>
      {children}
    </RequireRole>
  );
}

/**
 * JWT-only AdminRoute (Phase 1).
 * - Uses /v1/auth/me via AuthProvider
 * - Redirects to /login when unauthenticated
 * - Redirects to /forbidden when authenticated but lacks admin roles
 */

function AdminRouteJwtOnly(props: { children: JSX.Element }) {
  const { children } = props;
  const { state } = useAuth();
  const loc = useLocation();

  // Only enforce authentication here.
  // Authorization/role gating is handled inside AdminRouter via RequirePermission,
  // so operators can access observability, and admins/platform-admins can access everything else.
  const isAuthed = Boolean(state.token && state.user);

  if (!isAuthed) {
    // If token exists but needs provisioning, send to provisioning instead of login.
    if (state.token && state.needsProvisioning) {
      return <Navigate to="/provision" replace />;
    }
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

/**
 * Redirect helper:
 * /app/admin/<rest> -> /admin/<rest>
 * Keeps a canonical admin router mounted at /admin/*,
 * while preserving older /app/admin/* deep-links.
 */
function AppAdminRedirect() {
  const loc = useLocation();
  const path = loc.pathname || "";
  const rest = path.replace(/^\/app\/admin\/?/, ""); // e.g. "providers", "workflows", ""
  const target = rest ? `/admin/${rest}` : "/admin/overview";
  return <Navigate to={target} replace />;
}

function ProvisionGate({ children }: { children: JSX.Element }) {
  const { state } = useAuth();
  const loc = useLocation();

  const hasToken = Boolean(state.token);

  // If token exists and backend says NO_MEMBERSHIP, force provisioning flow.
  // Do NOT require state.user here; NO_MEMBERSHIP happens before user/tenant are set.
  if (hasToken && state.needsProvisioning && loc.pathname !== "/provision") {
    return <Navigate to="/provision" replace />;
  }

  return children;
}

export default function App() {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [idErr, setIdErr] = useState<string | null>(null);

  // Keep existing identity fetch (legacy /app/admin/* pages still rely on it)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await fetchIdentity();
        if (alive) setIdentity(me);
      } catch (e: any) {
        if (alive) {
          setIdentity({ user: null, tenant: "public", roles: [] });
          setIdErr(e?.message ?? null);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <AuthProvider>
      <QueryClientProvider client={qc}>
        <ErrorBoundary>
          {idErr && (
            <div style={{ padding: 12, background: "#FFF4E5", color: "#7A4D00", fontSize: 12 }}>
              Identity load warning: {idErr}
            </div>
          )}

          <Suspense fallback={<div style={{ padding: 16 }}>Loading…</div>}>
            <ProvisionGate>
              <Routes>
                {/* -----------------------------------------
                    Auth UX (Phase 1)
                  ----------------------------------------- */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forbidden" element={<ForbiddenPage />} />
                <Route path="/provision" element={<ProvisionTenantPage />} />

                {/* -----------------------------------------
                    Public
                  ----------------------------------------- */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/app/chat" element={<ChatPage />} />

                {/* -----------------------------------------
                    Ingress Runner (End-user)
                  ----------------------------------------- */}
                <Route path="/app/ingress/apps/:slug" element={<IngressRunnerPage />} />
                <Route path="/app/ingress/:slug" element={<IngressRunnerPage />} />
                <Route path="/app/ingress" element={<IngressLandingPage />} />
                <Route path="/app/ingress/:slug/chat" element={<IngressChatPage />} />

                {/* -----------------------------------------
                    Canonical Admin Router (JWT-only)
                  ----------------------------------------- */}
                <Route
                  path="/admin/*"
                  element={
                    <AdminRouteJwtOnly>
                      <AdminRouter />
                    </AdminRouteJwtOnly>
                  }
                />

                {/* -----------------------------------------
                    Back-compat: /app/admin/* -> /admin/*
                  ----------------------------------------- */}
                <Route
                  path="/app/admin/workflows"
                  element={
                    <AdminRoute identity={identity}>
                      <WorkflowComposerPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/app/admin/*"
                  element={
                    <AdminRouteJwtOnly>
                      <AppAdminRedirect />
                    </AdminRouteJwtOnly>
                  }
                />

                {/* -----------------------------------------
                    Existing individual admin pages
                  ----------------------------------------- */}
                <Route path="/app/admin/models" element={<AdminRoute identity={identity}><AdminModels /></AdminRoute>} />
                <Route path="/app/admin/pricing" element={<AdminRoute identity={identity}><AdminPricing /></AdminRoute>} />
                <Route path="/app/admin/policies" element={<AdminRoute identity={identity}><AdminPolicies /></AdminRoute>} />
                <Route path="/app/admin/observability" element={<AdminRoute identity={identity}><ObservabilityPage /></AdminRoute>} />
                <Route path="/app/admin/memory" element={<AdminRoute identity={identity}><MemoryPage /></AdminRoute>} />
                <Route path="/app/admin/feature-flags" element={<AdminRoute identity={identity}><FeatureFlagSwitchboard /></AdminRoute>} />

                {/* Convenience redirects / 404 */}
                <Route path="/app" element={<Navigate to="/" replace />} />
                <Route path="*" element={<div style={{ padding: 16 }}>404: Page not found</div>} />
              </Routes>
            </ProvisionGate>

            <DevToolsPanel />
          </Suspense>
        </ErrorBoundary>
      </QueryClientProvider>
    </AuthProvider>
  );
}